import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, LIKERT } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_TIMER = 20;
const DEFAULT_RESPONSE = 3;

export default function TestFlow() {
  const nav = useNavigate();
  const sessionData = JSON.parse(sessionStorage.getItem("msis_session") || "null");
  const [phase, setPhase] = useState("instructions"); // instructions | item | submitting
  const [instructions, setInstructions] = useState("");
  const [timerLimit, setTimerLimit] = useState(DEFAULT_TIMER);
  const [items] = useState(sessionData?.items || []);
  const [responses, setResponses] = useState(sessionData?.responses || []);
  const [idx, setIdx] = useState(sessionData?.current_index || 0);
  const [selected, setSelected] = useState(null);
  const [timeLeft, setTimeLeft] = useState(DEFAULT_TIMER);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!sessionData) { nav("/take-test"); return; }
    api.get("/public/settings").then((r) => {
      setInstructions(r.data.instructions || "");
      setTimerLimit(r.data.timer_seconds || DEFAULT_TIMER);
      setTimeLeft(r.data.timer_seconds || DEFAULT_TIMER);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const total = items.length;
  const cur = items[idx];
  const progress = total ? Math.round((idx / total) * 100) : 0;

  // Timer
  useEffect(() => {
    if (phase !== "item" || !cur) return;
    setSelected(null);
    setTimeLeft(timerLimit);
    clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(tickRef.current); autoAdvance(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [phase, idx]); // eslint-disable-line

  const sendResponse = async (value) => {
    try {
      await api.post("/test/save-response", {
        test_id: sessionData.test_id, email: sessionData.email,
        item_no: cur.item_no, value,
      });
    } catch (e) { /* offline fallback */ }
  };

  const autoAdvance = async () => {
    const val = selected ?? DEFAULT_RESPONSE;
    await commitAndAdvance(val);
  };

  const commitAndAdvance = async (val) => {
    clearInterval(tickRef.current);
    await sendResponse(val);
    const next = [...responses.filter(r => r.item_no !== cur.item_no), { item_no: cur.item_no, value: val }];
    setResponses(next);
    sessionStorage.setItem("msis_session", JSON.stringify({ ...sessionData, responses: next, current_index: idx + 1 }));
    if (idx + 1 >= total) { await submit(next); return; }
    setIdx(idx + 1);
  };

  const submit = async (finalResponses) => {
    setPhase("submitting");
    try {
      const r = await api.post("/test/submit", { test_id: sessionData.test_id, email: sessionData.email });
      sessionStorage.removeItem("msis_session");
      nav(`/test/result/${r.data.report_id}`);
    } catch (e) {
      toast.error("Submission failed. Please try again.");
      setPhase("item");
    }
  };

  if (!sessionData) return null;

  if (phase === "instructions") {
    return (
      <div className="min-h-screen bg-white">
        <div className="container-msis py-12 max-w-3xl">
          <div className="overline">Instructions</div>
          <h1 className="font-display font-bold text-3xl tracking-tight mt-2">Before you begin</h1>
          <div className="mt-6 whitespace-pre-line text-sm text-slate2 leading-relaxed border border-border p-6 rounded-md" data-testid="instructions-text">{instructions}</div>
          <div className="mt-6 flex justify-end">
            <button className="btn-primary" onClick={() => setPhase("item")} data-testid="start-the-test-btn">Start the Test
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "submitting") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="overline">Generating your report</div>
          <div className="font-display font-bold text-2xl mt-2">Submitting your responses…</div>
        </div>
      </div>
    );
  }

  const isLast = idx === total - 1;
  const lowTime = timeLeft <= 5;

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      {/* Top progress bar */}
      <div className="bg-white border-b border-border shrink-0">
        <div className="container-msis py-2.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-navy text-white flex items-center justify-center font-display font-bold text-xs">M</div>
            <div className="text-xs text-slate2 font-mono"><span data-testid="item-counter">{idx + 1} / {total}</span></div>
          </div>
          <div className="flex-1 max-w-md">
            <div className="h-1 bg-subtle rounded">
              <div className="h-full bg-navy transition-all" style={{ width: `${((idx + 1) / total) * 100}%` }} />
            </div>
          </div>
          <Timer timeLeft={timeLeft} limit={timerLimit} low={lowTime} />
        </div>
      </div>

      <div className="flex-1 container-msis py-4 sm:py-6 max-w-3xl flex flex-col min-h-0">
        <div className="overline mb-2" data-testid="item-overline">Item {idx + 1} of {total}</div>
        <h2 className="font-display text-lg sm:text-xl md:text-2xl tracking-tight leading-snug text-ink" data-testid="item-text">{cur?.text}</h2>

        <div className="mt-4 space-y-2 flex-1" data-testid="likert-options">
          {LIKERT.map((opt) => (
            <button key={opt.value} type="button"
              onClick={() => setSelected(opt.value)}
              className={`likert-option-compact ${selected === opt.value ? "selected" : ""}`}
              data-testid={`likert-${opt.value}`}>
              <span className="likert-num-sm">{opt.value}</span>
              <span className="font-medium text-ink text-sm sm:text-base">{opt.label}</span>
            </button>
          ))}
        </div>

        <div className="mt-3 sm:mt-4 pt-3 border-t border-border flex items-center justify-between shrink-0">
          <div className="text-[11px] sm:text-xs text-slate2 hidden sm:block">No previous · Auto-advance on timeout · Default = Neutral</div>
          <button
            className="btn-primary ml-auto !py-2.5 !px-5"
            onClick={() => commitAndAdvance(selected ?? DEFAULT_RESPONSE)}
            data-testid={isLast ? "submit-btn" : "next-btn"}>
            {isLast ? "Submit" : "Next"}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function Timer({ timeLeft, limit, low }) {
  const pct = (timeLeft / limit) * 100;
  return (
    <div className={`flex items-center gap-2 ${low ? "timer-low" : "text-ink"}`} data-testid="timer">
      <svg width="32" height="32" viewBox="0 0 36 36" className="timer-ring">
        <circle cx="18" cy="18" r="15" fill="none" stroke="#E4E4E7" strokeWidth="3" />
        <circle cx="18" cy="18" r="15" fill="none" stroke="currentColor" strokeWidth="3"
          strokeDasharray={`${(pct * 94.2) / 100}, 94.2`} strokeLinecap="round" />
      </svg>
      <div className="font-mono text-sm tabular-nums w-8">{String(timeLeft).padStart(2, "0")}s</div>
    </div>
  );
}
